import { useEffect, useState } from 'react';
import { istNativ } from '../lib/plattform';
import { NavLink, Outlet } from 'react-router-dom';
import { useAuth } from '../lib/AuthContext';
import { useThema } from '../lib/theme';
import { ANBIETER_URL, POWERED_BY } from '../lib/marke';
import { LogoKachel } from '../../bereiche/firma/komponenten/Logo';
import { useSprache, SPRACHEN } from '../lib/i18n';
import type { SprachCode } from '../lib/i18n';
import { GRUPPEN, UEBERSICHT } from '../lib/navigation';
import type { NavEintrag } from '../lib/navigation';
import Schnellzugriff from './Schnellzugriff';
import Icon from './Icon';
import Glocke from '../../bereiche/aufgaben/komponenten/Glocke';
import { useBenachrichtigungen } from '../../bereiche/aufgaben/lib/benachrichtigungen';
import { useSchmal } from '../lib/schmal';
import AppRahmen from '../../app/AppRahmen';
import Tableiste from '../../app/Tableiste';
import Kachelbrett from '../../app/Kachelbrett';
import Seitenkopf, { KopfKnopf } from '../../app/Seitenkopf';
import { titelSchluessel } from '../lib/navigation';
import type { IconName } from './Icon';
import type { Ziel } from './Schnellzugriff';

// Hell/Dunkel war vorher ein Auswahlfeld ganz unten. Jetzt drei Knoepfe: der
// gewuenschte Zustand ist mit einem Klick erreichbar und sofort ablesbar.
export function ThemaSchalter({ kompakt = false }: { kompakt?: boolean }) {
  const { thema, setThema } = useThema();
  const { t } = useSprache();

  const stufen: Array<{ wert: 'hell' | 'dunkel' | 'system'; icon: IconName; titel: string }> = [
    { wert: 'hell',   icon: 'sonne',   titel: t('allg.thema.hell') },
    { wert: 'dunkel', icon: 'mond',    titel: t('allg.thema.dunkel') },
    { wert: 'system', icon: 'monitor', titel: t('allg.thema.system') },
  ];

  return (
    <div
      role="group"
      aria-label={t('allg.darstellung')}
      className={`inline-flex rounded-lg border border-slate-200 bg-slate-50 p-0.5 dark:border-slate-700 dark:bg-slate-950 ${kompakt ? '' : 'w-full'}`}
    >
      {stufen.map(st => {
        const aktiv = thema === st.wert;
        return (
          <button
            key={st.wert}
            onClick={() => setThema(st.wert)}
            title={st.titel}
            aria-label={st.titel}
            aria-pressed={aktiv}
            className={`flex flex-1 items-center justify-center rounded-md px-2.5 py-1.5 transition ${
              aktiv
                ? 'bg-white text-marke-600 shadow-sm dark:bg-slate-800 dark:text-marke-400'
                : 'text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200'}`}
          >
            <Icon name={st.icon} groesse={16} />
          </button>
        );
      })}
    </div>
  );
}

export default function Layout() {
  const { benutzer, mandant, flags, abmelden, istOwner, istAdmin } = useAuth();
  const schmal = useSchmal();
  const { t, sprache, setSprache } = useSprache();
  // Chat-Zähler gehört an den Menüpunkt, alles andere in die Glocke.
  const { anzahlChat, support } = useBenachrichtigungen();
  const [menuOffen, setMenuOffen] = useState(false);
  const [paletteOffen, setPaletteOffen] = useState(false);

  const erlaubt = (e: NavEintrag) => (!e.nurOwner || istOwner) && (!e.nurAdmin || istAdmin)
    && !(e.nichtInHandyApp && istNativ());
  const gesperrt = (e: NavEintrag) => (e.flag ? !flags?.[e.flag] : false);

  const gruppen = GRUPPEN
    .map(g => ({ ...g, eintraege: g.eintraege.filter(erlaubt) }))
    .filter(g => g.eintraege.length > 0);

  const alleZiele: Ziel[] = [UEBERSICHT, ...gruppen.flatMap(g => g.eintraege)]
    .map(e => ({ pfad: e.pfad, schluessel: e.schluessel, symbol: e.symbol, gesperrt: gesperrt(e) }));

  // Strg/Cmd + K öffnet den Schnellzugriff, überall in der Anwendung.
  useEffect(() => {
    function beiTaste(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaletteOffen(o => !o);
      }
    }
    window.addEventListener('keydown', beiTaste);
    return () => window.removeEventListener('keydown', beiTaste);
  }, []);

  const eintrag = (e: NavEintrag) => (
    <li key={e.pfad}>
      <NavLink
        to={e.pfad}
        end={e.pfad === '/'}
        onClick={() => setMenuOffen(false)}
        className={({ isActive }) => [
          'flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition',
          isActive
            ? 'bg-marke-50 font-medium text-marke-700 dark:bg-marke-950/50 dark:text-marke-300'
            : 'text-slate-700 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800',
        ].join(' ')}
      >
        <Icon name={e.symbol} groesse={18} />
        <span className="flex-1">{t(e.schluessel)}</span>
        {e.pfad === '/chat' && anzahlChat > 0 && (
          <span className="grid h-5 min-w-5 place-items-center rounded-full bg-rose-600 px-1.5 text-[10px] font-bold text-white">
            {anzahlChat > 99 ? '99+' : anzahlChat}
          </span>
        )}
        {e.pfad === '/support' && support.length > 0 && (
          <span className="grid h-5 min-w-5 place-items-center rounded-full bg-marke-600 px-1.5 text-[10px] font-bold text-white">
            {support.length}
          </span>
        )}
        {gesperrt(e) && (
          <span
            className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-500 dark:bg-slate-800 dark:text-slate-400"
            title={t('allg.nichtImTarif')}
          >
            {t('allg.tarif')}
          </span>
        )}
      </NavLink>
    </li>
  );

  // --- App-Ansicht -----------------------------------------------------
  // Unterhalb von lg gibt es keine Seitenleiste und keine Burger-Kopfzeile
  // mehr, sondern einen Seitenstapel mit Tableiste. Oberhalb bleibt alles
  // unverändert — der Zweig darunter ist Zeile für Zeile der alte.
  if (schmal) {

    return (
      <div className="flex h-dvh min-h-0 flex-col overflow-hidden bg-slate-50 dark:bg-slate-950">
        <Schnellzugriff ziele={alleZiele} offen={paletteOffen} schliessen={() => setPaletteOffen(false)} />
        <AppRahmen
          wurzelInhalt={<Kachelbrett suchen={() => setPaletteOffen(true)} />}
          rahmen={(inhalt, tief, pfad) => {
            // Titel und Wurzel-Entscheidung gehören zur JEWEILIGEN Ebene.
            // Vorher trugen alle Ebenen den Titel der obersten Seite — beim
            // Wischen stand über dem Kachelbrett eine fremde Leiste.
            const schluessel = titelSchluessel(pfad);
            const aufWurzel = pfad === '/';
            return (
            <>
              {/* Das Kachelbrett bringt seinen Kopf selbst mit; alle anderen
                  Seiten bekommen ihn hier, damit keine Seite angefasst
                  werden muss. */}
              {!aufWurzel && (
                <Seitenkopf
                  titel={schluessel ? t(schluessel) : t('app.name')}
                  zurueck={tief > 0 ? () => window.history.back() : undefined}
                  aktionen={<KopfKnopf symbol="suche" beschriftung={t('aktion.suchen')}
                                       onClick={() => setPaletteOffen(true)} />}
                />
              )}
              <div data-rumpf className="flex-1 overflow-y-auto">
                {aufWurzel ? inhalt : <div className="p-4">{inhalt}</div>}
              </div>
            </>
            );
          }}
        />
        <Tableiste />
      </div>
    );
  }

  return (
    <div className="flex min-h-full flex-col lg:flex-row">
      <Schnellzugriff ziele={alleZiele} offen={paletteOffen} schliessen={() => setPaletteOffen(false)} />

      {/*
        Kopfzeile für schmale Bildschirme.

        Der obere Abstand kommt als Innenabstand aus der sicheren Zone: in der
        App läuft der Inhalt bis unter die Statusleiste, und ein Außenabstand
        würde den weißen Streifen der Kopfzeile dort abreißen lassen. So wächst
        die Kopfzeile stattdessen nach oben und schließt sauber mit dem
        Bildschirmrand ab.
      */}
      <header
        style={{ paddingTop: 'calc(0.75rem + var(--sicher-oben))' }}
        className="flex items-center justify-between border-b border-slate-200 bg-white px-4 pb-3 lg:hidden dark:border-slate-800 dark:bg-slate-900"
      >
        <span className="flex items-center gap-2 font-semibold text-slate-900 dark:text-slate-100">
          <LogoKachel groesse="klein" />
          {t('app.name')}
        </span>
        <div className="flex items-center gap-1">
          <Glocke />
          <ThemaSchalter kompakt />
          <button
            onClick={() => setPaletteOffen(true)}
            className="rounded-lg p-2 text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800"
            aria-label={t('allg.schnellzugriff')}
          ><Icon name="suche" /></button>
          <button
            onClick={() => setMenuOffen(o => !o)}
            className="rounded-lg p-2 text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800"
            aria-expanded={menuOffen}
            aria-label={t('allg.menue')}
          ><Icon name={menuOffen ? 'schliessen' : 'menue'} /></button>
        </div>
      </header>

      <nav
        className={`${menuOffen ? 'block' : 'hidden'} shrink-0 overflow-y-auto border-b border-slate-200 bg-white px-3 py-4 lg:block lg:h-screen lg:w-64 lg:border-e lg:border-b-0 dark:border-slate-800 dark:bg-slate-900`}
        aria-label={t('allg.menue')}
      >
        <div className="mb-4 hidden items-center gap-2.5 px-2 lg:flex">
          <LogoKachel />
          <div className="min-w-0">
            <p className="text-lg font-bold leading-tight text-slate-900 dark:text-slate-100">{t('app.name')}</p>
            <p className="truncate text-xs text-slate-500 dark:text-slate-400">{mandant?.name}</p>
          </div>
        </div>

        {/* Schnellzugriff sichtbar machen — sonst findet die Tastenkombination niemand. */}
        <button
          onClick={() => setPaletteOffen(true)}
          className="mb-4 hidden w-full items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-500 hover:bg-slate-50 lg:flex dark:border-slate-700 dark:text-slate-400 dark:hover:bg-slate-800"
        >
          <Icon name="suche" groesse={16} />
          <span className="flex-1 text-start">{t('aktion.suchen')}</span>
          <kbd className="rounded border border-slate-300 px-1.5 py-0.5 text-[10px] dark:border-slate-600">⌘K</kbd>
        </button>

        <ul className="mb-3 space-y-0.5">{eintrag(UEBERSICHT)}</ul>

        {gruppen.map(g => (
          <div key={g.titel} className="mb-3">
            <p className="mb-1 px-3 text-[11px] font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500">
              {t(g.titel)}
            </p>
            <ul className="space-y-0.5">{g.eintraege.map(eintrag)}</ul>
          </div>
        ))}

        <div className="mt-6 space-y-3 border-t border-slate-200 px-2 pt-4 dark:border-slate-800">
          <div>
            <p className="truncate text-sm font-medium text-slate-900 dark:text-slate-100">{benutzer?.name}</p>
            <p className="truncate text-xs text-slate-500 dark:text-slate-400">
              {istOwner ? t('allg.rolle.inhaber')
                : benutzer?.role === 'admin' ? t('allg.rolle.admin')
                : t('allg.rolle.mitarbeiter')}
            </p>
          </div>

          <label className="block">
            <span className="mb-1 block text-xs text-slate-500 dark:text-slate-400">{t('allg.sprache')}</span>
            <select
              value={sprache}
              onChange={e => setSprache(e.target.value as SprachCode)}
              className="w-full rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-sm text-slate-900 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
            >
              {SPRACHEN.map(s => (
                <option key={s.code} value={s.code}>{s.flagge} {s.name}</option>
              ))}
            </select>
          </label>

          <div>
            <span className="mb-1 block text-xs text-slate-500 dark:text-slate-400">{t('allg.darstellung')}</span>
            <ThemaSchalter />
          </div>

          <button
            onClick={abmelden}
            className="w-full rounded-lg px-3 py-2 text-start text-sm text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800"
          >
            {t('aktion.abmelden')}
          </button>

          {/* Anbieterhinweis am Fuß der Navigation — sichtbar, ohne sich
              zwischen die täglichen Wege zu stellen. */}
          <a href={ANBIETER_URL} target="_blank" rel="noopener noreferrer"
             className="block px-3 pt-2 text-[11px] leading-relaxed text-slate-400 hover:text-slate-600 hover:underline dark:text-slate-500 dark:hover:text-slate-300">
            {POWERED_BY}
          </a>
        </div>
      </nav>

      <div className="flex min-w-0 flex-1 flex-col lg:h-screen">
        {/* Auf großen Bildschirmen eine schmale Leiste — die Glocke gehört
            dorthin, wo der Blick ohnehin hinfällt, nicht in die Seitenleiste. */}
        <div className="hidden items-center justify-end border-b border-slate-200 bg-white px-6 py-2 lg:flex dark:border-slate-800 dark:bg-slate-900">
          <Glocke />
        </div>
        {/* Unten Platz für die Home-Leiste, sonst liegt der letzte Knopf
            darunter und lässt sich nicht treffen. */}
        <main
          style={{ paddingBottom: 'calc(1rem + var(--sicher-unten))' }}
          className="min-w-0 flex-1 p-4 lg:overflow-y-auto lg:p-8"
        >
          <Outlet />
        </main>
      </div>
    </div>
  );
}
